import { apiClient } from '@indipia/shared/api/client';
import { API_PRX } from '@indipia/shared/api/endpoints';
import type { ApiResponse } from '@indipia/shared-types';
import type {
  CompanyInfo,
  PhotoSearchResult,
  ProjectDetail,
  ProjectListItem,
  ProjectUpsertRequest,
  SiteDetailLite,
  SiteOption,
} from '../types/portfolio.types';

interface PhotoSearchParams {
  keyword?: string;
  siteId?: number;
  startDate?: string;
  endDate?: string;
  offset?: number;
  limit?: number;
}

/** 인디포트폴리오(sv-portfolio) API — FE 호출 경로 /api/portfolio/... (설계 §8-2) */
export const portfolioApi = {
  /** 시공사례 목록 (관리). siteId 지정 시 그 현장 사례만(현장 작업물 슬라이드오버). */
  getProjects(siteId?: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<ProjectListItem[]>>(`${API_PRX.PORTFOLIO}/projects`, {
      params: siteId ? { siteId } : undefined,
      signal,
    });
  },

  /** 사례 단건 (편집 로드) */
  getProject(projectId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<ProjectDetail>>(`${API_PRX.PORTFOLIO}/projects/${projectId}`, { signal });
  },

  /** 사례 작성 → projectId 반환 */
  createProject(body: ProjectUpsertRequest, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<number>>(`${API_PRX.PORTFOLIO}/projects`, body, { signal });
  },

  /** 사례 수정 */
  updateProject(projectId: number, body: ProjectUpsertRequest, signal?: AbortSignal) {
    return apiClient.put<ApiResponse<void>>(`${API_PRX.PORTFOLIO}/projects/${projectId}`, body, { signal });
  },

  /** 사진 검색 (sv-photo 위임) — 현장별 그룹 응답 */
  searchPhotos(params: PhotoSearchParams, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<PhotoSearchResult>>(`${API_PRX.PORTFOLIO}/photos`, { params, signal });
  },

  /**
   * 현장 목록 (sv-customer 위임) — 작성 진입 시 현장 선택용.
   * memberId 를 주면 그 회원이 현장 담당자 또는 관리자인 현장만(sv-customer 가 거른 뒤 limit 적용).
   */
  getSites(memberId?: number | null, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<{ items: SiteOption[] }>>(`${API_PRX.CUSTOMER}/sites`, {
      params: { limit: 200, sort: 'created', ...(memberId != null ? { memberIds: String(memberId) } : {}) },
      signal,
    });
  },

  /** 현장 상세 (sv-customer 위임) — 위치(단지명)·평수 자동채움 */
  getSiteDetail(siteId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<SiteDetailLite>>(`${API_PRX.CUSTOMER}/sites/${siteId}`, { signal });
  },

  /** 내 업체 공개 정보 — 에디터 회사정보 섹션 */
  getCompany(signal?: AbortSignal) {
    return apiClient.get<ApiResponse<CompanyInfo>>(`${API_PRX.PORTFOLIO}/company`, { signal });
  },

  /** 썸네일/이미지 로드 — sv-photo 이미지가 로그인 토큰 필요라 apiClient(JWT)로 blob 취득(인디포토 getThumb과 동일) */
  getThumb(url: string, signal?: AbortSignal) {
    return apiClient.get<Blob>(url, { responseType: 'blob', signal });
  },

  /** 작성자 미리보기 HTML (공개/비공개 무관) — 서버 렌더 결과 문자열 */
  previewProject(projectId: number, signal?: AbortSignal) {
    return apiClient.get<string>(`${API_PRX.PORTFOLIO}/projects/${projectId}/preview`, {
      responseType: 'text',
      signal,
    });
  },

  /** 공개 발행 */
  publish(projectId: number, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<void>>(`${API_PRX.PORTFOLIO}/projects/${projectId}/publish`, undefined, { signal });
  },

  /** 비공개 전환 */
  unpublish(projectId: number, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<void>>(`${API_PRX.PORTFOLIO}/projects/${projectId}/unpublish`, undefined, { signal });
  },

  /** 삭제(소프트) */
  remove(projectId: number, signal?: AbortSignal) {
    return apiClient.delete<ApiResponse<void>>(`${API_PRX.PORTFOLIO}/projects/${projectId}`, { signal });
  },

  /** SNS 포스팅 완료 표시 토글 */
  setPosted(projectId: number, posted: boolean, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<void>>(`${API_PRX.PORTFOLIO}/projects/${projectId}/posted`, { posted }, { signal });
  },
};
